import JsSIP from 'jssip'
// redux imports
import store from 'store'
import { authError } from 'redux/Auth'
import { parkSession, warmTransferPending } from 'redux/Sessions'
import { contactSelectedClear } from 'redux/Contacts'
// module imports
import { numberFromContactSimple } from 'modules/Contact'
import { userAgent as UA } from 'modules/Browser'
import {
  outgoingCallConnected,
  showErrorSnackbar,
} from 'modules/GlobalEvents/EventDispatchers'
// constant imports
import { NO_SIP } from 'constants/Errors'
import { REFER_CODES } from 'constants/Sessions'
// local imports
import { transferEvents } from './Sessions'
import { setEventListeners } from './Events'

// https://jssip.net/documentation/3.4.x/api/ua

export const setSIPDebug = (enable) => {
  if (enable) {
    JsSIP.debug.enable('JsSIP:*')
  } else {
    JsSIP.debug.disable('JsSIP:*')
  }
}

export const createConfig = async () => {
  const {
    config: {
      ua: { UA_SOCKET, UA_URI, UA_TRANSPORT },
      debug,
    },
    sip: { config },
  } = store.getState()
  // By default use hardcoded socket url

  if (!(config.username && config.password)) {
    store.dispatch(authError(NO_SIP))
    return Promise.reject(NO_SIP)
  }

  // set sip debug
  setSIPDebug(debug?.enable && debug?.SIPVerbose)
  // create websocket
  const socket = await new JsSIP.WebSocketInterface(UA_SOCKET)
  // create contact_uri for CS3.0
  const host = `${JsSIP.Utils.createRandomToken(12)}.invalid`
  const uri = new JsSIP.URI('sip', config.username, host, null, {
    transport: UA_TRANSPORT,
  })
  // update config
  return Promise.resolve({
    ...config,
    sockets: [socket],
    uri: `${config.username}@${UA_URI}`,
    contact_uri: uri.toString(),
    user_agent: UA,
  })
}

export const createUserAgent = async () => {
  const {
    sip: { config },
  } = store.getState()

  // create user agent
  const userAgent = await new JsSIP.UA(config)

  // start user agent
  await userAgent.start()

  setEventListeners(userAgent)

  return Promise.resolve(userAgent)
}

export const makeCall = (isWarmTransfer = false) => {
  const {
    sip: { userAgent, sessionOptions },
    contacts: { selected },
    config: {
      debug: { enable, fake },
    },
  } = store.getState()

  if (enable && fake) return

  const number = numberFromContactSimple(selected)

  if (number) {
    const session = userAgent.call(number, {
      ...sessionOptions,
      eventHandlers: {
        failed: (e) => {
          const showError = e.cause === JsSIP.C.causes.SIP_FAILURE_CODE
          if (showError) {
            showErrorSnackbar('NOTIFICATIONS.FAILED', 'call-failed')
          }
        },
        accepted: () => {
          if (!isWarmTransfer) {
            outgoingCallConnected()
          }
        },
      },
    })

    if (isWarmTransfer) {
      // needs _request call_id otherwise from_tag is appended to id
      // eslint-disable-next-line no-underscore-dangle
      store.dispatch(warmTransferPending(session._request.call_id))
    }
  } else {
    showErrorSnackbar('NOTIFICATIONS.FAILED', 'call-failed')
  }

  store.dispatch(contactSelectedClear())
}

export const transferSession = (
  id = 0,
  transferToNumber,
  transferNotify = true,
  replacementId = undefined,
) => {
  const sessions = store.getState().sessions.list
  const { session } = sessions[id] || {}
  const { session: replacementSession } = sessions[replacementId] || {}

  if (!session) return id

  const {
    sip: { sessionOptions },
    contacts: { selected },
    config: {
      debug: { enable, fake },
    },
  } = store.getState()

  if (enable && fake) return id

  // eslint-disable-next-line camelcase
  const from = session?.local_identity?.toString()
  // create refer to
  const referTo = transferToNumber || numberFromContactSimple(selected)
  // event handlers
  const eventHandlers = transferEvents(transferNotify)

  const refer = () => {
    session.refer(referTo, {
      eventHandlers,
      extraHeaders: [`Referred-By: ${from}`],
      mediaConstraints: sessionOptions.mediaConstraints,
      pcConfig: sessionOptions.pcConfig,
      replaces: replacementSession,
    })
  }

  // place on hold before referring (if not on hold)
  session.isOnHold().local ? refer() : session.hold({}, refer)

  return id
}

export const referToCode = (id = 0, action, suffix = '') => {
  // get correct code
  const code = {
    [REFER_CODES.PARK.ACTION]: REFER_CODES.PARK.CODE,
    [REFER_CODES.TRANSFER_TO_VOICEMAIL.ACTION]:
      REFER_CODES.TRANSFER_TO_VOICEMAIL.CODE,
  }[action]

  const referTo = code + suffix

  const notify = action !== REFER_CODES.PARK.ACTION

  transferSession(id, referTo, notify)
}

export const unregister = async () => {
  const { userAgent } = store.getState().sip
  const { ready } = store.getState().config

  let response

  if (ready) {
    userAgent.terminateSessions()
    userAgent.stop()
    response = 'terminated'
  } else if (userAgent?.on) {
    userAgent.on('registered', () => {
      userAgent.stop()
    })
    response = 'unregistered'
  } else {
    response = { ready, userAgent }
  }

  return Promise.resolve(response)
}

export const parkSessionAction = (id) => {
  store.dispatch(parkSession(id))
}
